import { ContentUsage } from "./contentUsage";
import { ExamTicket } from "./exam";
import { AnyObject, IDSK, ParsedItem } from "./misc";
import {
  AcademicCalendar,
  Annotation,
  App,
  AppDomain,
  AppKey,
  AppLicense,
  AppUserConfig,
  Classroom,
  ClassroomUnit,
  ClassroomUser,
  ContentLicense,
  ContentRegistrationCode,
  Course,
  CourseCalendar,
  ExamAttempt,
  ExamSession,
  LaunchToken,
  Resource,
  RestApiToken,
  Unit,
  User,
  UserClassroom,
  Vocabulary,
  Workspace,
  WorkspaceParent,
  Product,
  WorkspaceRegistrationCode,
  WorkspaceShare,
} from "./schema";
import { AnyResource } from "./util";
import { WorkspaceUser } from "./workspace";

type RelationshipRecord = Pick<
  Resource,
  | "resourceId"
  | "createdAt"
  | "updatedAt"
  | "sk"
  | "id"
  | "workspaceId"
  | "schemaVersion"
>;

export const isIDSK = (obj: any): obj is IDSK =>
  typeof obj?.id === "string" && typeof obj?.sk === "string";

/**
 * Higher-order function to create a generic type constraint function
 * that can handle subtypes like Parsed* etc
 *
 * So that you can have something like:
 *
 *  const wsu: ParsedWorkspaceUser | Workspace = getWorkspaceOrUser();
 *  if(isWorkspaceUser(wsu)) {
 *    // types for `wsu.acl` are correct based on the ParsedWorkspaceUser type
 *    console.log(wsu.acl.role);
 *  }
 */
const createSkTypeCheck =
  <TResult extends IDSK, TSK extends string = string>(
    sk: string,
    opts?: { exact: boolean },
  ) =>
  <T extends TResult>(
    obj: T | IDSK | null | undefined,
  ): obj is T & { sk: TSK } =>
    opts?.exact ? obj?.sk === sk : !!obj?.sk.startsWith(sk);

export const isAcademicCalendar = createSkTypeCheck<AcademicCalendar>(
  "academic-calendar#v",
);
export const isApp = createSkTypeCheck<App>("app", { exact: true });
export const isAppDomain = createSkTypeCheck<AppDomain>("app-domain", {
  exact: true,
});
export const isAppKey = createSkTypeCheck<AppKey>("app-key", { exact: true });
export const isAppLicense = createSkTypeCheck<AppLicense>("app-license#");
export const isAppUserConfig =
  createSkTypeCheck<AppUserConfig>("app-user-config#");
export const isAlignmentDocument = createSkTypeCheck<
  Resource,
  `alignment#v${number}`
>("alignment#v");
export const isAnnotation = createSkTypeCheck<
  Annotation,
  `annotation#${string}`
>("annotation#");

export const isBank = createSkTypeCheck<Resource, `bank#${string}`>("bank#");
export const isBlueprint = createSkTypeCheck<Resource, `blueprintv${number}`>(
  "blueprint#v",
);

export const isClassroom = createSkTypeCheck<Classroom>("classroom", {
  exact: true,
});
export const isClassroomPlannerItem = createSkTypeCheck<
  Resource,
  `classroom-planner-item:${string}`
>("classroom-planner-item:");
export const isClassroomActivity = createSkTypeCheck<Classroom>(
  "classroom-activity#",
);
export const isClassroomUnit =
  createSkTypeCheck<ClassroomUnit>("classroom-unit#");
export const isClassroomUser =
  createSkTypeCheck<ClassroomUser>("classroom-user#");
export const isCollection = createSkTypeCheck<
  Resource,
  `collection#v${number}`
>("collection#v");
export const isCollectionItem = createSkTypeCheck<
  Resource,
  `collection-item:${string}`
>("collection-item:");

export const isComment = createSkTypeCheck<Resource, `comment#${string}`>(
  "comment#",
);
export const isContextItem = createSkTypeCheck<
  RelationshipRecord,
  `context-item:${string}`
>("context-item:");
export const isCourse = createSkTypeCheck<Course>("course#v");
export const isCourseCalendar =
  createSkTypeCheck<CourseCalendar>("course-calendar#");
export const isCoursePlan = createSkTypeCheck<
  Resource,
  `course-plan#v${number}`
>("course-plan#v");
export const isCourseResource = createSkTypeCheck<
  RelationshipRecord,
  `course-resource:${string}`
>("course-resource:");
export const isCourseVerification = createSkTypeCheck<
  AnyResource,
  `course-verification#v${number}`
>("course-verification#v");
export const isCourseUnitLayout = createSkTypeCheck<
  AnyResource,
  `course#unit-layout#${string}`
>("course#unit-layout#");

export const isExam = createSkTypeCheck<Resource, `exam#v${number}`>("exam#v");
export const isExamConfig = createSkTypeCheck<Resource, "exam-config">(
  "exam-config",
  {
    exact: true,
  },
);
export const isExamExamConfig = createSkTypeCheck<
  Resource,
  `exam-config:${string}`
>("exam-config:");
export const isExamConfigSession = createSkTypeCheck<
  Resource,
  `exam-config-session#${string}`
>("exam-config-session#");
export const isExamResponse = createSkTypeCheck<
  Resource,
  `exam-response:${string}`
>("exam-response:");
export function isExamAttempt(obj: any): obj is ExamAttempt {
  const parts = obj.sk.split("/");
  return parts[1]?.startsWith("exam-session#exam-attempt");
}
export const isExamSession = createSkTypeCheck<ExamSession, "exam-session">(
  "exam-session",
  {
    exact: true,
  },
);
export const isExamTicket = createSkTypeCheck<ExamTicket, "exam-ticket">(
  "exam-ticket",
  {
    exact: true,
  },
);
export const isExamUnit = createSkTypeCheck<
  RelationshipRecord,
  `exam-unit:${string}`
>("exam-unit:");

export const isEventLogItem = createSkTypeCheck<
  Resource,
  `event-log-item#${string}`
>("event-log-item#");

export const isFavorite = createSkTypeCheck<
  RelationshipRecord,
  `favorite#${string}`
>("favorite#");
export const isFile = createSkTypeCheck<Resource, `file#${string}`>("file#");

export const isIssueLookup = createSkTypeCheck<Resource, `issue:${string}`>(
  "issue:",
);
export const isIssue = createSkTypeCheck<Resource, `issue`>("issue", {
  exact: true,
});
export const isIssueKey = createSkTypeCheck<Resource, `issue-key#${string}`>(
  "issue-key#",
);
export const isItem = createSkTypeCheck<ParsedItem, `item#v${number}`>(
  "item#v",
);
export const isItemCollection = createSkTypeCheck<
  RelationshipRecord,
  `item-collection:${string}`
>("item-collection:");
export const isItemContext = createSkTypeCheck<
  RelationshipRecord,
  `item-context:${string}`
>("item-context:");
export const isItemUnit = createSkTypeCheck<
  RelationshipRecord,
  `item-unit:${string}`
>("item-unit:");

export const isLaunchToken = createSkTypeCheck<LaunchToken>("launch-token", {
  exact: true,
});

export const isObjectivesView = createSkTypeCheck<
  Resource,
  `objectives-view#v${number}`
>("objectives-view#v");
export const isOutlet = createSkTypeCheck<Resource, `outlet#${string}`>(
  "outlet#",
);
export const isOutputTask = createSkTypeCheck<
  Resource,
  `output-task:${string}`
>("output-task:");

export const isProof = createSkTypeCheck<Resource, `proof#${string}`>("proof#");
export const isProduct = createSkTypeCheck<Product>("product", { exact: true });

export const isContentRegistrationCode =
  createSkTypeCheck<ContentRegistrationCode>("content-registration-code", {
    exact: true,
  });
export const isContentLicense =
  createSkTypeCheck<ContentLicense>("content-license#");

export const isRegistrationCode =
  createSkTypeCheck<WorkspaceRegistrationCode>("registration-code#");
export const isRegistrationDomain = createSkTypeCheck<
  Resource,
  `registration-domain#${string}`
>("registration-domain#");
export const isRole = createSkTypeCheck<Resource, "role">("role", {
  exact: true,
});
export const isRolePolicy = createSkTypeCheck<
  Resource,
  `role-policy#${string}`
>("role-policy#");
export const isRelative = createSkTypeCheck<
  RelationshipRecord,
  `relative:${string}`
>("relative:");
export const isResource = createSkTypeCheck<Resource, `resource#v${number}`>(
  "resource#v",
);
export const isResourceCourse = createSkTypeCheck<
  Resource,
  `resource-course:${string}`
>("resource-course:");
export const isResourceTask = createSkTypeCheck<
  Resource,
  `resource-task:${string}`
>("resource-task:");
export const isResourceUnit = createSkTypeCheck<
  Resource,
  `resource-unit:${string}`
>("resource-unit:");

export const isStudent = createSkTypeCheck<Resource, `student#${string}`>(
  "student#",
);
export const isStudentAssignmentAccessCode = createSkTypeCheck<
  Resource,
  `student-assignment-access-code#${string}`
>("student-assignment-access-code#");
export const isSystemUpdateExamItemStats = (
  obj: AnyResource,
): obj is Resource & { id: "_system:update-exam-item-stats" } => {
  return obj.id === "_system:update_exam_item_stats";
};

export const isTask = createSkTypeCheck<Resource, "task">("task", {
  exact: true,
});
export const isTaskResource = createSkTypeCheck<
  Resource,
  `task-resource:#${string}`
>("task-resource:");
export const isTaskOutput = createSkTypeCheck<
  Resource,
  `task-output:${string}`
>("task-output:");
export const isTaskAuditItem = createSkTypeCheck<
  Resource,
  `task-audit-item:${string}`
>("task-audit-item:");
export const isAuditItemTask = createSkTypeCheck<
  Resource,
  `audit-item-task:${string}`
>("audit-item-task:");

export const isUnit = createSkTypeCheck<Unit>("unit#v");
export const isUnitExam = createSkTypeCheck<
  RelationshipRecord,
  `unit-exam:${string}`
>("unit-exam:");
export const isUnitItem = createSkTypeCheck<
  RelationshipRecord,
  `unit-item:${string}`
>("unit-item:");
export const isUnitResource = createSkTypeCheck<
  RelationshipRecord,
  `unit-resource:${string}`
>("unit-resource:");
export const isUser = createSkTypeCheck<User, "user">("user", { exact: true });
export const isUserClassroom =
  createSkTypeCheck<UserClassroom>("user-classroom#");
export const isUserNotification = createSkTypeCheck<
  User,
  `user-notification#${string}`
>("user-notification#");
export const isUserNotificationUnread = createSkTypeCheck<
  User,
  `user-notification-unread#${string}`
>("user-notification-unread#");

export const isVendorId = createSkTypeCheck<Resource, `vendor-id#${string}`>(
  "vendor-id#",
);
export const isVocabulary = createSkTypeCheck<Vocabulary>("vocabulary", {
  exact: true,
});

export const isWorkspace = createSkTypeCheck<Workspace, "workspace">(
  "workspace",
  {
    exact: true,
  },
);
export const isInvitation = createSkTypeCheck<Resource, `invitation#${string}`>(
  "invitation#",
);
export const isWorkspaceParent = createSkTypeCheck<WorkspaceParent>(
  "workspace-parent",
  {
    exact: true,
  },
);
export const isWorkspaceShare =
  createSkTypeCheck<WorkspaceShare>("workspace-share#");
export const isWorkspaceUser = createSkTypeCheck<
  WorkspaceUser,
  `workspace-user#${string}`
>("workspace-user#");

export const isContentUsage = createSkTypeCheck<ContentUsage>("content-usage:");

/**
 * The predicates belonw came over from cli/workspace:reset.
 *
 * Not doing a bunch of refactor on these yet, just trying to at least move all
 * predicates in the codebase to a single file.
 */

export const isClassroomRecord = (item: AnyObject) => {
  if (item.sk.match(/^academic-calendar/)) return true;
  if (item.sk.match(/^classroom$/)) return true;
  if (item.sk.match(/^classroom-activity#/)) return true;
  if (item.sk.match(/^classroom-unit#/)) return true;
  if (item.sk.match(/^classroom-user#/)) return true;
  if (item.sk.match(/^course-calendar#/)) return true;
  if (item.sk.match(/^user-classroom#/)) return true;
};

export const isLogRecord = (item: AnyObject) => {
  if (item.sk.match(/^event-log-item#/)) return true;
  if (item.sk.match(/^revision-note#/)) return true;
};

export const isWorkspaceRecord = (item: AnyObject) => {
  if (item.sk.match(/^workspace$/)) return true;
  if (item.sk.match(/^registration-code#/)) return true;
};

export const isWorkspaceUserRecord = (item: AnyObject) => {
  if (item.sk.match(/^workspace-user#/)) return true;
  if (item.sk.match(/^role/)) return true;
};

export const isBankRecord = (item: AnyObject) => {
  if (item.sk.match(/^bank#/)) return true;
};

export const isAppRecord = (item: AnyObject) => {
  if (item.sk.match(/^app-key$/)) return true;
  if (item.sk.match(/^app-license#/)) return true;
  if (item.sk.match(/^app#/)) return true;
  if (item.sk.match(/^app$/)) return true;
};

export const isAppUserRecord = (item: AnyObject) => {
  if (item.sk.match(/^app-user-config#/)) return true;
};

export const isFavoriteRecord = (item: AnyObject) => {
  if (item.sk.match(/^favorite#/)) return true;
};

export const isClassroomExam = (item: AnyObject) => {
  if (item.sk.match(/^exam-config$/)) return true;
  if (item.sk.match(/^exam-config:/)) return true;
  if (item.sk.match(/^exam-config-session#/)) return true;
  if (item.sk.match(/^exam-session$/)) return true;
  if (item.sk.match(/^exam-ticket$/)) return true;
  if (item.sk.match(/^exam-ticket#/)) return true;
  if (item.sk.match(/^exam-response:/)) return true;
  if (item.sk.match(/^classroom-activity#/)) return true;
  if (isExamAttempt(item)) return true;
};

export const isExamSessionRecord = (item: AnyObject) => {
  if (item.sk.match(/^exam-config-session#/)) return true;
  if (item.sk.match(/^exam-session$/)) return true;
  if (item.sk.match(/^exam-response:/)) return true;
  if (isExamAttempt(item)) return true;
};

export const isContentRecord = (item: AnyObject) => {
  if (item.sk.match(/^alignment#/)) return true;
  if (item.sk.match(/^context-item:/)) return true;
  if (item.sk.match(/^collection#v0$/)) return true;
  if (item.sk.match(/^course/)) return true;
  if (item.sk.match(/^exam#/)) return true;
  if (item.sk.match(/^exam-unit:/)) return true;
  if (item.sk.match(/^exam-ticket$/)) return true;
  if (item.sk.match(/^file#/)) return true;
  if (item.sk.match(/^item-context:/)) return true;
  if (item.sk.match(/^item-unit:/)) return true;
  if (item.sk.match(/^item#v0/)) return true;
  if (item.sk.match(/^objectives-view/)) return true;
  if (item.sk.match(/^product$/)) return true;
  if (item.sk.match(/^relative:/)) return true;
  if (item.sk.match(/^resource-course/)) return true;
  if (item.sk.match(/^resource-unit/)) return true;
  if (item.sk.match(/^resource#v0/)) return true;
  if (item.sk.match(/^unit/)) return true;
  if (item.sk.match(/^unit-exam:/)) return true;
  if (item.sk.match(/^vendor-id/)) return true;
  if (item.sk.match(/^vocabulary/)) return true;
};

export const isRestApiToken = createSkTypeCheck<RestApiToken, `rest-api-token`>(
  "rest-api-token",
  { exact: true },
);
